import React, { FC, useMemo } from "react"
import {
  Box,
  BoxProps,
  ComponentWithAs,
  Flex,
  Heading,
  Icon,
  IconProps,
  Text,
  useToken,
} from "@chakra-ui/react"
import { motion } from "framer-motion"

import { BasePageWrapper } from "~/components/shared/BasePageWrapper/BasePageWrapper"
import { PresentationImage, PresentationImageType } from "~/components/shared/PresentationImage"
import {
  NextButton,
  NextButtonBottomSlideUpContainer as NextButtonContainer,
} from "~/components/shared/NextButton"
import { FramerBox } from "~/components/shared/FramerBox"
import { WellnessProfileScreen_Level } from "~/generated/interview_service"

import { Feather, Goal, Smile, Sun } from "./Icons"
import { Good, Low, Moderate } from "./TooltipIcons"
import { SliderRainbow } from "./SliderRainbow"
import { ReactComponent as TooltipTail } from "./assets/TooltipTail.svg"
import { ReactComponent as HeaderLow } from "./assets/HeaderLow.svg"
import { ReactComponent as HeaderModerate } from "./assets/HeaderModerate.svg"
import { ReactComponent as HeaderGood } from "./assets/HeaderGood.svg"

const LEVELS_DATA = {
  low: {
    color: "Other/Error",
    icon: Low,
    title: "Your skin needs intensive care",
    description: "Start your journey to healthier skin and achieve dream results.",
    header: {
      component: HeaderLow,
      color: "Other/Error",
    },
  },
  moderate: {
    color: "Other/Warning",
    icon: Moderate,
    title: "Your skin needs extra care",
    description:
      "Begin a healthy journey with expert tutorials, and personalized routines. Hydrate and rest!",
    header: {
      component: HeaderModerate,
      color: "Other/Warning",
    },
  },
  good: {
    color: "Other/Green",
    icon: Good,
    title: "Your skin needs essential care",
    description:
      "Maintain results and protect your skin from environmental exposure to preserve its vitality.",
    header: {
      component: HeaderGood,
      color: "Other/Green",
    },
  },
} as const

type Level = keyof typeof LEVELS_DATA

const mapLevelToSliderValue = (level: Level): number => {
  switch (level) {
    case "low":
      return 0.1
    case "moderate":
      return 0.5
    case "good":
      return 0.9
  }

  return 0.1
}

const mapEnumToLevel = (levelEnum: WellnessProfileScreen_Level): Level => {
  switch (levelEnum) {
    case WellnessProfileScreen_Level.LOW:
      return "low"
    case WellnessProfileScreen_Level.MODERATE:
      return "moderate"
  }

  return "good"
}

const VARIANTS = {
  list: {
    visible: {
      transition: {
        delayChildren: 1,
        staggerChildren: 0.2,
        ease: "easeInOut",
      },
    },
    hidden: {},
  },

  item: {
    visible: { opacity: 1, x: "0%", transition: { duration: 0.5 } },
    hidden: { opacity: 0, x: "-50%" },
  },
}

export const GoalsListItem: FC<{
  icon: ComponentWithAs<"svg", IconProps>
  title: string
  description: string
}> = ({ icon, title, description }) => (
  <motion.div variants={VARIANTS.item}>
    <Flex direction="column" gap={1} alignSelf="stretch">
      <Flex direction="row" color="Base/baseSecondary" gap={0.5} alignItems="center">
        <Icon as={icon} w="16px" h="16px" />
        <Box textStyle={{ base: "Paragraph/Tertiary", md: "Paragraph/Secondary" }}>{title}</Box>
      </Flex>
      <Box textStyle={{ base: "Subtitle/Tertiary", md: "Subtitle/Secondary" }}>{description}</Box>
    </Flex>
  </motion.div>
)

export const GoalsList: FC<{ data: Record<string, string> }> = ({ data }) => (
  <FramerBox
    initial="hidden"
    animate="visible"
    variants={VARIANTS.list}
    display="flex"
    flexDirection="column"
    flexGrow={1}
    gap={4}
    alignSelf="stretch"
    ml={4}
  >
    {data["skin_type"] && (
      <GoalsListItem icon={Smile} title="Skin type" description={data["skin_type"]} />
    )}
    {data["main_goal"] && (
      <GoalsListItem title="Main goal" icon={Goal} description={data["main_goal"]} />
    )}
    {data["skin_sensitivity"] && (
      <GoalsListItem
        title="Skin sensitivity"
        icon={Feather}
        description={data["skin_sensitivity"]}
      />
    )}
    {data["exposure_category"] && (
      <GoalsListItem title="Exposure category" icon={Sun} description={data["exposure_category"]} />
    )}
  </FramerBox>
)

export const HeaderExtended: FC<{ level: Level }> = ({ level }) => {
  const h = LEVELS_DATA[level].header
  const Component = h.component
  const [color] = useToken("colors", [h.color])
  return (
    <Heading textStyle={{ base: "Header/Primary", md: "Lead/Secondary" }}>
      You have
      <Text
        as={motion.span}
        initial={{ color: "#F2F5F7" }}
        animate={{ color }}
        transition="0.5s ease-in-out 0.25s"
        display="inline-block"
        position="absolute"
        marginTop={{ base: "-10px", md: "-16px" }}
        marginLeft="4px"
      >
        <Component />
      </Text>
      <br />
      Skin Wellness Level
    </Heading>
  )
}

const FakeTooltip: FC<{ color: string; level: number } & BoxProps> = ({
  color,
  level,
  ...props
}) => (
  <Box>
    <Box color={color} paddingLeft={`calc(${level * 100}% - 24px - 24px + 10px)`} marginX="24px">
      <TooltipTail />
    </Box>
    <Box
      borderRadius="brand24"
      padding={4}
      w="full"
      color="Base/neutralPrimary"
      bgColor={color}
      {...props}
    />
  </Box>
)

export const SliderWithTooltip: FC<{ level: Level }> = ({ level }) => {
  const { color, title, description, icon } = LEVELS_DATA[level]
  return (
    <Box>
      <FramerBox
        initial={{ x: "-50%", opacity: 0 }}
        animate={{ x: "0%", opacity: 1 }}
        /* @ts-ignore: Incorrect type from chakra.div */
        transition={{ delay: 0.5, duration: 0.25, ease: "easeInOut" }}
      >
        <SliderRainbow value={1 - mapLevelToSliderValue(level)} color={color} />
      </FramerBox>
      <FramerBox
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        /* @ts-ignore: Incorrect type from chakra.div */
        transition={{ delay: 0.5, duration: 0.5, ease: "easeInOut" }}
      >
        <FakeTooltip color={color} level={mapLevelToSliderValue(level)}>
          <Flex direction="row" gap={4} alignItems="center">
            <Icon as={icon} w="56px" h="56px" color={color} />
            <Flex direction="column" gap={1}>
              <Box textStyle="Other/Price2">{title}</Box>
              <Box textStyle="Paragraph/Secondary">{description}</Box>
            </Flex>
          </Flex>
        </FakeTooltip>
      </FramerBox>
    </Box>
  )
}
export const AgeImage: FC<{ source: PresentationImageType; title: string }> = ({
  source,
  title,
}) => (
  <FramerBox
    initial={{ x: "100%", opacity: 0 }}
    animate={{ x: "0%", opacity: 1 }}
    /* @ts-ignore: Incorrect type from chakra.div */
    transition={{ delay: 1, duration: 0.5, ease: "easeInOut" }}
  >
    <Flex direction="column" gap={2} alignItems="center" justifyContent="start">
      <PresentationImage source={source} w="155" h="175" alt="" objectFit="scale-down" />
      <Box textStyle="Paragraph/Tertiary" color="Base/baseSecondary">
        {title}
      </Box>
    </Flex>
  </FramerBox>
)

export const WellnessProfilePage: FC<{
  level: WellnessProfileScreen_Level
  properties: Record<string, string>
  ageImage?: PresentationImageType
  ageTitle?: string
  next: () => void
}> = ({ level: levelEnum, properties = {}, ageImage, ageTitle, next }) => {
  const level = useMemo(() => mapEnumToLevel(levelEnum), [levelEnum])

  return (
    <BasePageWrapper>
      <Flex direction="column" gap="20px" alignSelf="stretch">
        <HeaderExtended level={level} />
        <SliderWithTooltip level={level} />
        <Flex direction="row" gap={2} alignSelf="stretch">
          <GoalsList data={properties} />
          {ageImage && ageTitle && <AgeImage source={ageImage} title={ageTitle} />}
        </Flex>
      </Flex>
      <NextButtonContainer visible enterDelay={2}>
        <NextButton onClick={next} />
      </NextButtonContainer>
    </BasePageWrapper>
  )
}
